import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

import { listCrmActions, type CrmLead, type CrmTodaySummary } from "../../api";

type OutletCtx = {
  summary: CrmTodaySummary | null;
  summaryError: string | null;
  reloadSummary: () => void;
};

function verdictClass(v: string): string {
  if (v === "bad") return "bg-red-600 text-white";
  if (v === "mid") return "bg-amber-500 text-white";
  return "bg-emerald-600 text-white";
}

function numberTone(v: string): string {
  if (v === "bad") return "text-red-100";
  if (v === "mid") return "text-amber-100";
  return "text-emerald-100";
}

export default function CrmToday() {
  const { summary, summaryError } = useOutletContext<OutletCtx>();
  const [actions, setActions] = useState<CrmLead[]>([]);
  const [actionsErr, setActionsErr] = useState<string | null>(null);

  useEffect(() => {
    listCrmActions()
      .then((r) => {
        setActions(r.items || []);
        setActionsErr(null);
      })
      .catch((e: unknown) => {
        setActions([]);
        setActionsErr(e instanceof Error ? e.message : "Erreur actions");
      });
  }, []);

  if (summaryError) {
    return (
      <div className="rounded-xl border border-stone bg-white p-6 text-concrete">
        Impossible de charger le tableau de bord : {summaryError}
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="rounded-xl border border-stone bg-white p-6 text-concrete">
        Chargement…
      </div>
    );
  }

  const { touchpoint, pipeline, due_actions: dueActions } = summary;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-concrete">
            Aujourd&apos;hui
          </p>
          <h1 className="font-sans text-3xl font-bold tracking-tight text-black">
            Priorités &amp; scores
          </h1>
        </div>
        {dueActions > 0 && (
          <p className="font-mono text-xs text-concrete">
            {dueActions} action{dueActions > 1 ? "s" : ""} due
          </p>
        )}
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {[touchpoint, pipeline].map((card) => (
          <div
            key={card.label}
            className="relative flex min-h-[180px] flex-col justify-between overflow-hidden rounded-[10px] bg-black p-6 text-ivory"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-[11px] uppercase tracking-wider text-ash">
                {card.label}
              </span>
              <span
                className={`rounded px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide ${verdictClass(card.verdict)}`}
              >
                {card.verdict}
              </span>
            </div>
            <p
              className={`my-3 font-sans text-7xl font-extrabold leading-none tracking-tighter ${numberTone(card.verdict)}`}
            >
              {card.pct}
              <span className="text-4xl text-ash">%</span>
            </p>
            <p className="font-mono text-[10px] text-ash">
              Données Dynamo (<code className="text-lemonade">ORG#default</code> /{" "}
              <code className="text-lemonade">TODAY</code>) — vide = 0%.
            </p>
          </div>
        ))}
      </div>

      <section className="rounded-xl border border-stone bg-white p-6 shadow-sm">
        <h2 className="font-mono text-[11px] uppercase tracking-wider text-concrete">
          File d&apos;actions
        </h2>
        {actionsErr ? (
          <p className="mt-2 text-red-700">Impossible de charger : {actionsErr}</p>
        ) : actions.length === 0 ? (
          <p className="mt-2 text-concrete">Aucune action pour l’instant.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {actions.slice(0, 30).map((a) => {
              const due = a.next_step_at ? new Date(a.next_step_at) : null;
              return (
                <div
                  key={a.lead_id}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-4 rounded-lg border border-stone bg-white px-4 py-3"
                >
                  <div className="h-6 w-6 rounded-full border-2 border-ash" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <Link
                        to={`/admin/crm/leads/${a.lead_id}`}
                        className="truncate font-semibold text-charcoal hover:underline"
                      >
                        {a.first_name} {a.last_name || ""}
                      </Link>
                      <span className="font-mono text-[11px] text-concrete">
                        {a.company || "—"}
                      </span>
                    </div>
                    <div className="mt-0.5 text-sm text-charcoal">
                      {a.next_step_label || "—"}
                    </div>
                  </div>
                  <div className="text-right font-mono text-[11px] text-concrete whitespace-nowrap">
                    {due ? due.toLocaleDateString() : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
